import { yupResolver } from '@hookform/resolvers/yup';
import DoneIcon from '@mui/icons-material/Done';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { authContext, Position } from '@staffedupusa/shared-components';
import { FC, useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import AssistantIcon from '@mui/icons-material/Assistant';

import {
  ApplicationQuestion,
  predefinedQuestions,
  SubmitQuestion,
} from '@features/applicationQuestions';
import { usePostApplicationQuestion } from '@features/applicationQuestions/hooks/usePostApplicationQuestion';
import { useUpdateApplicationQuestion } from '@features/applicationQuestions/hooks/useUpdateApplicationQuestion';
import { useGetJobPostings } from '@features/jobPostings/hooks/useGetJobPostings';
import { medium } from '@styles/fontWeights';
import AssigningTagMainModal from '@components/AssigningTagMainModal/AssigningTagMainModal';
import BundledEditor from '@utils/BundledEditor';
import { Close, HelpOutline } from '@mui/icons-material';
import { useUpdateProfile } from '@features/profile/hooks/useUpdateProfile';
import { getStrippedText } from '@utils/helper';
import AIPromptModal from '@features/messaging/components/AIPromptModal/AIPromptModal';
import { AITooltipText } from '@features/messaging';

type JobPostingModalProps = {
  isOpen: boolean;
  fromOnboarding?: boolean;
  employerTagData: any;
  handleClose: () => void;
  applicationQuestion: ApplicationQuestion | null;
};

const schema: yup.ObjectSchema<SubmitQuestion> = yup
  .object({
    id: yup.number(),
    job_postings: yup.object({}),
    question: yup
      .string()
      .required('Question is required')
      .test('not-empty-html', 'Question cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
    disqualify: yup.boolean(),
    preferred_answer: yup.string().when('disqualify', {
      is: true,
      then: (stringSchema) =>
        stringSchema.oneOf(['Yes', 'No']).required('Must be selected'),
      otherwise: (stringSchema) => stringSchema.optional(),
    }),
    label: yup
      .string()
      .required('Label is required')
      .test('not-empty-html', 'Question cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
    question_type: yup.number().required('Question type must be selected'),
    all_positions_selected: yup.boolean(),
    eeo: yup.boolean(),
    job_posting_ids: yup.string().required('Please select a position'),
    yes_case_tags: yup.array().of(yup.number()).notRequired().default([]), // Optional array of numbers
    no_case_tags: yup.array().of(yup.number()).notRequired().default([]),
  })
  .required();

const ApplicationQuestionModal: FC<JobPostingModalProps> = ({
  isOpen,
  fromOnboarding,
  handleClose,
  employerTagData,
  applicationQuestion,
}) => {
  const { authData } = useContext(authContext);
  const [errorText, setErrorText] = useState<string>('');
  const [showManageModal, setShowManageModal] = useState<boolean>(false);
  const [showAiPromptModal, setShowAiPromptModal] = useState(false);
  const { updateProfile } = useUpdateProfile({});

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<SubmitQuestion>({
    resolver: yupResolver(schema),
    defaultValues: {
      id: undefined,
      job_postings: undefined,
      question: '',
      label: '',
      preferred_answer: 'None',
      disqualify: false,
      question_type: 1,
      all_positions_selected: false,
      eeo: false,
      job_posting_ids: '',
      yes_case_tags: [],
      no_case_tags: [],
    },
  });

  const label = watch('label');

  const { postApplicationQuestion, isPostApplicationQuestionLoading } =
    usePostApplicationQuestion({
      setErrorText,
    });
  const { updateApplicationQuestion, isUpdateApplicationQuestionLoading } =
    useUpdateApplicationQuestion({
      setErrorText,
    });

  const { jobPostingsData } = useGetJobPostings();

  const handleInsertAIContent = (content: string) => {
    setValue('question', content);
    setShowAiPromptModal(false);
  };
  const onSubmit = (values: SubmitQuestion) => {
    const payload = {
      ...values,
      disqualify: String((values as ApplicationQuestion)?.disqualify!),
      eeo: String((values as ApplicationQuestion)?.eeo! ?? false),
      all_positions_selected: String(
        (
          values as {
            job_posting_ids: string;
          }
        )
          .job_posting_ids!.split(',')
          .filter(Boolean).length ===
          jobPostingsData?.object?.filter((item: any) => item.status !== 4)
            ?.length,
      ),
    };

    const requestOptions = {
      onSuccess: (data: { status: number }) => {
        if (data?.status === 200) {
          if (
            !fromOnboarding &&
            authData?.user &&
            authData?.user?.company &&
            !authData?.user?.company?.check_list
              ?.has_added_pre_qualification_questions &&
            (values?.preferred_answer === 'No' ||
              values?.preferred_answer === 'Yes')
          ) {
            updateProfile({ has_added_pre_qualification_questions: true });
          }
          handleClose();
          reset();
          toast.success('Application Question Saved');
        }
      },
    };
    return applicationQuestion
      ? updateApplicationQuestion(
          {
            ...payload,
            id: applicationQuestion?.id as number,
          } as SubmitQuestion,
          requestOptions,
        )
      : postApplicationQuestion(payload as SubmitQuestion, requestOptions);
  };

  useEffect(() => {
    if (applicationQuestion) {
      setValue('question', applicationQuestion.question);
      setValue('label', applicationQuestion.label);
      setValue('preferred_answer', applicationQuestion.preferred_answer);
      setValue('disqualify', applicationQuestion.disqualify);
      setValue('question_type', applicationQuestion.question_type);
      setValue(
        'all_positions_selected',
        applicationQuestion.all_positions_selected,
      );
      setValue('eeo', applicationQuestion.eeo);
      setValue(
        'yes_case_tags',
        applicationQuestion?.yes_case_tags
          ? applicationQuestion.yes_case_tags.map((id) => Number(id))
          : [],
      );
      setValue(
        'no_case_tags',
        applicationQuestion?.no_case_tags
          ? applicationQuestion.no_case_tags.map((id) => Number(id))
          : [],
      );

      setValue(
        'job_posting_ids',
        applicationQuestion.job_postings
          .filter((item: any) => item.status !== 4)
          .map(({ id }) => String(id))
          .join(','),
      );
    } else {
      reset();
      if (jobPostingsData?.object?.length === 1) {
        setValue('job_posting_ids', String(jobPostingsData.object[0].id));
      }
    }
  }, [applicationQuestion, jobPostingsData, setValue, reset]);

  useEffect(() => {
    if (!applicationQuestion) {
      const referredTag = employerTagData?.objects.find(
        (item: { tag: string }) => item.tag === 'Reffered',
      );
      const currentTags = watch('yes_case_tags') || [];
      if (label === 'Referral') {
        setValue(
          'yes_case_tags',
          [...currentTags, referredTag?.id].filter(Boolean),
        );
      } else {
        const updatedTags = currentTags.filter((id) => id !== referredTag?.id);
        setValue('yes_case_tags', updatedTags);
      }
    }
  }, [
    applicationQuestion,
    jobPostingsData,
    setValue,
    reset,
    label,
    employerTagData,
  ]);

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        handleClose();
        setErrorText('');
        reset();
      }}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={() => {
            handleClose();
            setErrorText('');
            reset();
          }}
        >
          <Close />
        </IconButton>
        <br />
        <Typography variant="h4" sx={{ pt: 3 }}>
          {applicationQuestion
            ? 'Edit Application Question'
            : 'Add Application Question'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          {errorText && <Alert severity="error">{errorText}</Alert>}
          <form onSubmit={handleSubmit(onSubmit)} style={{ marginBottom: 10 }}>
            <Stack
              spacing={3}
              sx={{
                pt: 1,
                '& p': {
                  ml: 1,
                  fontSize: '15px',
                },
              }}
            >
              <Stack
                divider={<Divider flexItem />}
                spacing={2}
                direction={{ xs: 'column', sm: 'row' }}
              >
                <Autocomplete
                  options={predefinedQuestions}
                  fullWidth
                  onChange={(e, question) => {
                    if (question) {
                      setValue('label', question.label, {
                        shouldValidate: true,
                      });
                      setValue('question', question.question, {
                        shouldValidate: true,
                      });
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select from Pre-Drafted Questions"
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="label"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="Label"
                      hiddenLabel
                      variant="outlined"
                      size="small"
                      fullWidth
                      error={Boolean(errors.label)}
                      helperText={errors?.label ? errors?.label.message : ''}
                    />
                  )}
                />
              </Stack>

              <Controller
                control={control}
                name="question"
                render={({ field }) => (
                  <Stack spacing={1}>
                    <BundledEditor
                      {...field}
                      onEditorChange={(newValue: string) =>
                        field.onChange(newValue)
                      }
                      onChange={() => {}}
                      init={{
                        height: 200,
                        menubar: false,
                        plugins: ['lists', 'autolink', 'link', 'pagebreak'],
                        toolbar:
                          'spellchecker language spellcheckdialog | styles | bold italic | bullist numlist | link',
                        placeholder: 'Question',
                        spellchecker_dialog: true,
                        spellchecker_rpc_url: 'localhost/ephox-spelling',
                        spellchecker_language: 'en_US',
                        spellchecker_active: true,
                        browser_spellcheck: true,
                        contextmenu: false,
                      }}
                    />
                    {Boolean(errors?.question?.message) && (
                      <Typography color="error">
                        {errors?.question?.message}
                      </Typography>
                    )}
                    <Stack mt={1} spacing={2} direction="row" alignItems="center" justifyContent='end'>
                      <Button
                        variant="outlined"
                        size='small'
                        onClick={() => setShowAiPromptModal(true)}
                        startIcon={<AssistantIcon />}
                      >
                        Write with AI
                      </Button>
                      <Tooltip title={AITooltipText} placement="top" arrow>
                        <HelpOutline color="secondary" fontSize="small" />
                      </Tooltip>
                    </Stack>
                  </Stack>
                )}
              />
              <Stack spacing={0}>
                <Stack spacing={2} direction="row" alignItems="center">
                  <Typography fontWeight={medium} variant="subtitle1">
                    Answer Type:
                  </Typography>
                  {applicationQuestion && (
                    <Tooltip
                      title="The answer type of a question can not be changed. If you'd like to change the answer type of this question, please create a new question."
                      placement="right"
                      arrow
                    >
                      <HelpOutlineIcon />
                    </Tooltip>
                  )}
                </Stack>
                <Controller
                  control={control}
                  name="question_type"
                  render={({ field: { value, onChange } }) => (
                    <RadioGroup
                      row
                      value={value}
                      onChange={(e) => onChange(Number(e.target.value))}
                    >
                      <FormControlLabel
                        value={1}
                        control={
                          <Radio disabled={Boolean(applicationQuestion)} />
                        }
                        label="Yes / No"
                      />
                      <FormControlLabel
                        value={2}
                        control={
                          <Radio disabled={Boolean(applicationQuestion)} />
                        }
                        label="Freeform"
                        onChange={() => {
                          setValue('disqualify', false);
                          setValue('preferred_answer', 'None');
                          onChange(2);
                        }}
                      />
                    </RadioGroup>
                  )}
                />
              </Stack>

              <Controller
                control={control}
                name="job_posting_ids"
                render={({ field }) => {
                  const selectedIds = field.value.split(',').filter(Boolean);

                  return (
                    <Stack spacing={0}>
                      <Typography fontWeight={medium} variant="subtitle1">
                        Assign to:
                      </Typography>
                      <FormControl error={Boolean(errors.job_posting_ids)}>
                        <Select
                          {...field}
                          value={field.value.split(',')}
                          size="small"
                          multiple={jobPostingsData?.object.length !== 1}
                          displayEmpty
                          onChange={(e) => {
                            const valueArray = e.target.value as string[];
                            if (valueArray.includes('allPositions')) {
                              const allPositions = jobPostingsData?.object
                                ?.filter((item: any) => item.status !== 4)
                                .map(({ id }: Position) => String(id));
                              if (
                                field.value.split(',').length ===
                                allPositions.length
                              ) {
                                field.onChange('');
                              } else {
                                field.onChange(allPositions.join(','));
                              }
                            } else {
                              field.onChange(
                                valueArray.filter(Boolean).join(','),
                              );
                            }
                          }}
                          renderValue={(selected) => {
                            if (
                              jobPostingsData?.object?.filter(
                                (item: any) => item.status !== 4,
                              ).length === 1
                            ) {
                              return jobPostingsData?.object?.filter(
                                (item: any) => item.status !== 4,
                              )[0].s_title;
                            }

                            if (
                              selected.length ===
                              jobPostingsData?.object?.filter(
                                (item: any) => item.status !== 4,
                              ).length
                            ) {
                              return 'All Positions';
                            }

                            return (
                              jobPostingsData?.object
                                .filter(({ id }: Position) =>
                                  field.value.split(',').includes(String(id)),
                                )
                                .map(({ s_title }: Position) => s_title)
                                .join(', ') || 'Select Positions'
                            );
                          }}
                        >
                          {jobPostingsData?.object?.filter(
                            (item: any) => item.status !== 4,
                          ).length > 1 && (
                            <MenuItem
                              value="allPositions"
                              selected={
                                selectedIds.length ===
                                jobPostingsData?.object?.filter(
                                  (item: any) => item.status !== 4,
                                ).length
                              }
                            >
                              <Stack
                                spacing={1}
                                direction="row"
                                alignItems="center"
                              >
                                <Typography>All Positions</Typography>
                                <Tooltip
                                  title="If this option is chosen, this question will be applied to all current and future positions."
                                  placement="right"
                                  arrow
                                >
                                  <HelpOutlineIcon />
                                </Tooltip>
                              </Stack>
                            </MenuItem>
                          )}
                          {jobPostingsData?.object &&
                            jobPostingsData.object
                              ?.filter((item: any) => item.status !== 4)
                              .map((jobPosting: Position) => (
                                <MenuItem
                                  key={jobPosting.id}
                                  value={String(jobPosting.id)}
                                  disabled={
                                    jobPostingsData?.object.length === 1
                                  }
                                >
                                  {jobPosting.s_title}
                                </MenuItem>
                              ))}
                        </Select>
                        {errors?.job_posting_ids && (
                          <FormHelperText>
                            {errors?.job_posting_ids.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                      {
                        <>
                          {watch('question_type') === 1 && (
                            <Box mt={3}>
                              <Stack
                                spacing={2}
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <Typography
                                  fontWeight={medium}
                                  variant="subtitle1"
                                >
                                  Assign Tags:
                                </Typography>
                                <Button
                                  onClick={() => setShowManageModal(true)}
                                  color="success"
                                  variant="outlined"
                                >
                                  Manage Tags
                                </Button>
                              </Stack>
                              <Stack
                                spacing={2}
                                mt={3}
                                direction={{ xs: 'column', sm: 'row' }}
                              >
                                <Controller
                                  control={control}
                                  name="yes_case_tags"
                                  render={({ field: { onChange, value } }) => (
                                    <Autocomplete
                                      multiple
                                      sx={{ width: '100%' }}
                                      options={employerTagData?.objects || []}
                                      getOptionLabel={(option) => option.tag}
                                      isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                      }
                                      value={
                                        employerTagData?.objects?.filter(
                                          (tag: { id: number }) =>
                                            (value || []).includes(tag.id),
                                        ) || []
                                      }
                                      onChange={(_, selectedOptions) =>
                                        onChange(
                                          selectedOptions.map(
                                            (option) => option.id,
                                          ),
                                        )
                                      }
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="If Answer is Yes - Assign Tags"
                                          placeholder="Select tags"
                                          variant="outlined"
                                          size="small"
                                          fullWidth
                                        />
                                      )}
                                    />
                                  )}
                                />

                                <Controller
                                  control={control}
                                  name="no_case_tags"
                                  render={({ field: { onChange, value } }) => (
                                    <Autocomplete
                                      multiple
                                      sx={{ width: '100%' }}
                                      options={employerTagData?.objects || []}
                                      getOptionLabel={(option) => option.tag}
                                      isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                      }
                                      value={
                                        employerTagData?.objects?.filter(
                                          (tag: { id: number }) =>
                                            (value || []).includes(tag.id),
                                        ) || []
                                      }
                                      onChange={(_, selectedOptions) =>
                                        onChange(
                                          selectedOptions.map(
                                            (option) => option.id,
                                          ),
                                        )
                                      }
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="If Answers is No - Assign Tags"
                                          placeholder="Select tags"
                                          variant="outlined"
                                          size="small"
                                          fullWidth
                                        />
                                      )}
                                    />
                                  )}
                                />
                              </Stack>
                            </Box>
                          )}
                        </>
                      }
                    </Stack>
                  );
                }}
              />

              <Stack spacing={2} direction="row" alignItems="start">
                {watch('question_type') === 1 && (
                  <Stack spacing={0}>
                    <Stack spacing={2} direction="row" alignItems="center">
                      <Controller
                        control={control}
                        name="disqualify"
                        render={({ field: { value, onChange } }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={value}
                                onChange={(e) => {
                                  if (!e.target.checked) {
                                    setValue('preferred_answer', 'None');
                                  }

                                  onChange(e);
                                }}
                              />
                            }
                            label="Use for Pre-Qualification"
                          />
                        )}
                      />
                      <Tooltip
                        title="If an applicant does not answer with your preferred answer, their application will automatically be declined with your default decline message."
                        placement="right"
                        arrow
                      >
                        <HelpOutlineIcon />
                      </Tooltip>
                    </Stack>

                    {watch('disqualify') && (
                      <>
                        <Typography fontWeight={medium} variant="subtitle1">
                          Preferred Answer:
                        </Typography>
                        <Controller
                          control={control}
                          name="preferred_answer"
                          render={({ field: { value, onChange } }) => (
                            <RadioGroup
                              row
                              value={value}
                              onChange={(e) => onChange(e.target.value)}
                            >
                              <FormControlLabel
                                value="Yes"
                                control={<Radio />}
                                label="Yes"
                              />
                              <FormControlLabel
                                value="No"
                                control={<Radio />}
                                label="No"
                              />
                            </RadioGroup>
                          )}
                        />

                        {Boolean(errors?.preferred_answer?.message) && (
                          <Typography color="error">
                            Select one of these options.
                          </Typography>
                        )}
                      </>
                    )}
                  </Stack>
                )}
                <Stack spacing={2} direction="row" alignItems="center">
                  <Controller
                    control={control}
                    name="eeo"
                    render={({ field: { value, onChange } }) => (
                      <FormControlLabel
                        control={
                          <Checkbox checked={value} onChange={onChange} />
                        }
                        label="EEO"
                      />
                    )}
                  />
                  <Tooltip
                    title={
                      <Typography>
                        EEO (Equal Employment Opportunity) questions are
                        typically questions around race, gender, ethnicity, etc,
                        and are legally not allowed to be asked in the
                        application process. Certain companies are required to
                        ask EEO questions on job applications in order to file
                        their EEO-1 Report; a compliance survey mandated by
                        federal statute and regulations. Do not ask these
                        questions if your are not required to under this
                        mandate. <br />
                        <Link
                          target="_blank"
                          href="https://www.findmyprofession.com/career-advice/eeo-questions-everything-you-need-to-know/#:~:text=EEO%20(Equal%20Employment%20Opportunity)%20questions%20are%20most%20commonly%20found%20at,by%20federal%20statute%20and%20regulations"
                        >
                          (Learn More)
                        </Link>
                      </Typography>
                    }
                    placement="right"
                    arrow
                  >
                    <HelpOutlineIcon />
                  </Tooltip>
                </Stack>
              </Stack>

              <LoadingButton
                type="submit"
                startIcon={<DoneIcon />}
                variant="contained"
                size="small"
                fullWidth
                loading={
                  isPostApplicationQuestionLoading ||
                  isUpdateApplicationQuestionLoading
                }
                loadingPosition="start"
              >
                Save
              </LoadingButton>
            </Stack>
          </form>
        </Stack>
      </DialogContent>
      {showManageModal && (
        <AssigningTagMainModal
          handleClose={() => setShowManageModal(false)}
          isAssignTagModalOpen={showManageModal}
          manageTags
        />
      )}
      {showAiPromptModal && (
        <AIPromptModal
          isOpen={showAiPromptModal}
          handleInsertAIContent={handleInsertAIContent}
          handleClose={() => setShowAiPromptModal(false)}
        />
      )}
    </Dialog>
  );
};

export default ApplicationQuestionModal;
